// Commande à table : le client scanne le QR de sa table, commande depuis
// son téléphone et suit l'addition partagée de la table en temps réel.
// La fidélité est réservée à l'à-emporter : aucun compte client ici.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_URL, SOCKET_URL } from '../utils/api';
import { IconPlus, IconMinus, IconCheck } from '../components/icons';
import './DineInPage.css';

const CATEGORY_ORDER = ['Bouchées', 'Burgers', 'Plats', 'A partager', 'Enfants', 'Boissons', 'Desserts'];

const STATUS_FR = {
  pending: { label: 'Envoyé en cuisine', class: 'din-st-pending' },
  preparing: { label: 'En préparation', class: 'din-st-preparing' },
  ready: { label: 'Prêt, ça arrive !', class: 'din-st-ready' },
  completed: { label: 'Servi', class: 'din-st-done' },
  cancelled: { label: 'Annulé', class: 'din-st-cancelled' }
};

const euro = (n) => `${(Math.round(parseFloat(n || 0) * 100) / 100).toFixed(2)}€`;

// L'appareil se souvient de « sa » table (durée d'un repas) : si on scanne
// le QR d'une autre table par erreur (sortie fumer, terrasse…), on prévient.
const ACTIVE_TABLE_KEY = 'fief_active_table';
const ACTIVE_TABLE_TTL = 4 * 3600e3;

const getActiveTable = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(ACTIVE_TABLE_KEY));
    if (saved && saved.token && Date.now() - saved.ts < ACTIVE_TABLE_TTL) return saved;
  } catch { /* rien */ }
  return null;
};

function DineInPage() {
  const { qrToken } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState('loading'); // loading | invalid | ok
  const [table, setTable] = useState(null);
  const [tab, setTab] = useState(null);
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [view, setView] = useState('menu'); // menu | table
  const [cart, setCart] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(`dine_cart_${qrToken}`));
      return Array.isArray(saved) ? saved : [];
    } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [guestName, setGuestName] = useState(() => localStorage.getItem('fief_guest_name') || '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [justSent, setJustSent] = useState(false);
  const [addedId, setAddedId] = useState(null);
  const [tableConflict, setTableConflict] = useState(null); // {token, name} : l'appareil était sur une autre table
  const [optionsFor, setOptionsFor] = useState(null);       // produit dont on choisit l'option (Picon, sirop…)
  const [confirmNewTab, setConfirmNewTab] = useState(false); // 1er envoi = ouverture d'addition : on confirme la table
  const tableRef = useRef(null);
  tableRef.current = table;

  useEffect(() => {
    sessionStorage.setItem(`dine_cart_${qrToken}`, JSON.stringify(cart));
  }, [cart, qrToken]);

  const loadTab = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/dine/table/${qrToken}/tab`);
      if (res.ok) {
        const body = await res.json();
        setTab(body.tab);
      }
    } catch {
      // le socket ou une prochaine action rechargera
    }
  }, [qrToken]);

  // Chargement initial : table + carte + addition
  useEffect(() => {
    (async () => {
      try {
        const [tRes, pRes] = await Promise.all([
          fetch(`${API_URL}/dine/table/${qrToken}`),
          fetch(`${API_URL}/products`)
        ]);
        if (!tRes.ok) {
          setState('invalid');
          return;
        }
        const tBody = await tRes.json();
        setTable(tBody.table);
        const list = (await pRes.json()).filter(p => p.available);
        setProducts(list);
        const present = [...new Set(list.map(p => p.category))];
        const cats = [
          ...CATEGORY_ORDER.filter(c => present.includes(c)),
          ...present.filter(c => !CATEGORY_ORDER.includes(c))
        ];
        setCategory(cats[0] || null);
        setState('ok');
        loadTab();
      } catch {
        setState('invalid');
      }
    })();
  }, [qrToken, loadTab]);

  // Temps réel : l'addition de la table se met à jour toute seule
  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socket.on('tab-updated', (payload) => {
      const t = tableRef.current;
      if (t && payload && payload.diningTableId === t.id) {
        if (payload.status === 'open') {
          setTab(payload);
        } else {
          setTab(null); // encaissée ou annulée : la table repart à zéro
          const saved = getActiveTable();
          if (saved && saved.name === t.name) localStorage.removeItem(ACTIVE_TABLE_KEY);
        }
      }
    });
    return () => socket.disconnect();
  }, []);

  // On a scanné une AUTRE table alors que la nôtre a encore une addition
  // ouverte (sortie fumer, QR de la table voisine…) : on demande confirmation.
  useEffect(() => {
    if (state !== 'ok') return;
    const saved = getActiveTable();
    if (!saved || saved.token === qrToken) return;
    fetch(`${API_URL}/dine/table/${saved.token}`)
      .then(r => (r.ok ? r.json() : null))
      .then(body => {
        if (body && body.tab) {
          setTableConflict({ token: saved.token, name: body.table.name });
        } else {
          localStorage.removeItem(ACTIVE_TABLE_KEY); // addition soldée : contexte périmé
        }
      })
      .catch(() => {});
  }, [state, qrToken]);

  // Retour sur l'app (téléphone déverrouillé, onglet réaffiché…)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadTab();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadTab]);

  if (state === 'loading') {
    return <div className="din-splash"><img src="/logo.svg" alt="Le Fief" /><p>Un instant…</p></div>;
  }

  if (state === 'invalid') {
    return (
      <div className="din-splash">
        <img src="/logo.svg" alt="Le Fief" />
        <h1>QR code inconnu</h1>
        <p>Ce QR code n'est plus valide.<br />Appelez un serveur, il s'occupe de vous !</p>
        <Link to="/" className="btn btn-or">Voir le site du Fief</Link>
      </div>
    );
  }

  const present = [...new Set(products.map(p => p.category))];
  const cats = [
    ...CATEGORY_ORDER.filter(c => present.includes(c)),
    ...present.filter(c => !CATEGORY_ORDER.includes(c))
  ];

  // option = {name, price} choisie parmi p.options, ou null (nature)
  const addLine = (p, option = null) => {
    const optName = option ? option.name : null;
    setCart(prev => {
      const existing = prev.find(l => l.id === p.id && !l.notes && (l.option || null) === optName);
      if (existing) {
        return prev.map(l => l === existing ? { ...l, quantity: l.quantity + 1 } : l);
      }
      return [...prev, {
        uid: `${Date.now()}-${Math.random()}`,
        id: p.id,
        name: option ? `${p.name} — ${option.name}` : p.name,
        price: parseFloat(p.price) + (option ? parseFloat(option.price) || 0 : 0),
        option: optName,
        quantity: 1, notes: ''
      }];
    });
    setOptionsFor(null);
    setAddedId(p.id);
    setTimeout(() => setAddedId(null), 700);
  };

  // Un produit à options (boissons…) ouvre d'abord le choix
  const handleAdd = (p) => {
    if (Array.isArray(p.options) && p.options.length > 0) setOptionsFor(p);
    else addLine(p);
  };

  const setQty = (uid, q) => {
    setCart(prev => q <= 0 ? prev.filter(l => l.uid !== uid) : prev.map(l => l.uid === uid ? { ...l, quantity: q } : l));
  };

  const cartCount = cart.reduce((s, l) => s + l.quantity, 0);
  const cartTotal = cart.reduce((s, l) => s + parseFloat(l.price) * l.quantity, 0);

  const send = async () => {
    if (sending || cart.length === 0) return;
    // Premier envoi sur une table sans addition : on fait confirmer la table
    // (évite la bière commandée sur le QR de la terrasse en sortant fumer)
    if (!tab && !confirmNewTab) {
      setConfirmNewTab(true);
      return;
    }
    setConfirmNewTab(false);
    setSending(true);
    setError('');
    try {
      if (guestName) localStorage.setItem('fief_guest_name', guestName);
      const res = await fetch(`${API_URL}/dine/table/${qrToken}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart.map(l => ({
            id: l.id, quantity: l.quantity,
            option: l.option || undefined,
            notes: l.notes || undefined
          })),
          guestName: guestName || undefined
        })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body.message || (body.errors && body.errors[0] && body.errors[0].msg) || 'Envoi impossible, réessayez');
        return;
      }
      setCart([]);
      setCartOpen(false);
      setTab(body.tab);
      setView('table');
      setJustSent(true);
      // Cette table devient « ma table » pour la durée du repas
      localStorage.setItem(ACTIVE_TABLE_KEY, JSON.stringify({
        token: qrToken, name: table.name, ts: Date.now()
      }));
      setTimeout(() => setJustSent(false), 3500);
    } catch {
      setError('Pas de réseau… réessayez');
    } finally {
      setSending(false);
    }
  };

  const tabOrders = tab ? tab.orders.filter(o => o.status !== 'cancelled') : [];

  return (
    <div className="din-page">
      {/* En-tête */}
      <header className="din-head">
        <img src="/logo.svg" alt="Le Fief" className="din-logo" />
        <div className="din-head-table">
          <span className="din-head-label">Vous êtes à la</span>
          <strong>Table {table.name}</strong>
        </div>
      </header>

      {/* Sélecteur carte / table */}
      <div className="din-tabs">
        <button className={view === 'menu' ? 'on' : ''} onClick={() => setView('menu')}>
          La carte
        </button>
        <button className={view === 'table' ? 'on' : ''} onClick={() => { setView('table'); loadTab(); }}>
          Notre table {tab && tab.total > 0 ? `· ${euro(tab.total)}` : ''}
        </button>
      </div>

      {justSent && (
        <div className="din-sent">✅ C'est parti en cuisine !</div>
      )}

      {/* Choix d'option (bière + sirop, picon…) */}
      {optionsFor && (
        <div className="din-sheet-overlay" onClick={() => setOptionsFor(null)}>
          <div className="din-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="din-sheet-grip" />
            <h2>{optionsFor.name}</h2>
            <div className="din-options">
              <button className="din-option" onClick={() => addLine(optionsFor)}>
                <span>Nature</span>
                <strong>{euro(optionsFor.price)}</strong>
              </button>
              {optionsFor.options.map(o => (
                <button key={o.name} className="din-option" onClick={() => addLine(optionsFor, o)}>
                  <span>{o.name}</span>
                  <strong>{euro(parseFloat(optionsFor.price) + (parseFloat(o.price) || 0))}</strong>
                </button>
              ))}
            </div>
            <button className="din-sheet-close" onClick={() => setOptionsFor(null)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Premier envoi : on confirme qu'on est bien assis à cette table */}
      {confirmNewTab && (
        <div className="din-sheet-overlay" style={{ alignItems: 'center', padding: 20 }}>
          <div className="din-sheet" style={{ borderRadius: 22 }}>
            <h2>Vous êtes bien à la table {table.name} ?</h2>
            <p style={{ color: 'var(--texte-2)', margin: '8px 0 16px', lineHeight: 1.45 }}>
              Votre commande va ouvrir l'addition de la <strong>table {table.name}</strong>.
              Vérifiez que c'est bien le numéro affiché sur votre table.
            </p>
            <button className="btn btn-or btn-block btn-lg" disabled={sending} onClick={send}>
              Oui, envoyer en cuisine
            </button>
            <button className="din-sheet-close" onClick={() => setConfirmNewTab(false)}>
              Non, annuler
            </button>
          </div>
        </div>
      )}

      {/* Scan d'une autre table alors que la nôtre est encore ouverte */}
      {tableConflict && (
        <div className="din-sheet-overlay" style={{ alignItems: 'center', padding: 20 }}>
          <div className="din-sheet" style={{ borderRadius: 22 }}>
            <h2>Vous étiez à la table {tableConflict.name} 🤔</h2>
            <p style={{ color: 'var(--texte-2)', margin: '8px 0 16px', lineHeight: 1.45 }}>
              Votre addition est toujours ouverte sur la table {tableConflict.name}.
              Vous venez de scanner le QR de la <strong>table {table.name}</strong> —
              tout ce que vous commanderez ici sera ajouté à l'addition de la table {table.name}.
            </p>
            <button
              className="btn btn-or btn-block btn-lg"
              onClick={() => { setTableConflict(null); navigate(`/table/${tableConflict.token}`); }}
            >
              Revenir à ma table {tableConflict.name}
            </button>
            <button className="din-sheet-close" onClick={() => setTableConflict(null)}>
              J'ai vraiment changé de table
            </button>
          </div>
        </div>
      )}

      {/* ---- LA CARTE ---- */}
      {view === 'menu' && (
        <>
          <nav className="din-cats">
            {cats.map(c => (
              <button key={c} className={category === c ? 'on' : ''} onClick={() => setCategory(c)}>
                {c}
              </button>
            ))}
          </nav>

          <div className="din-products">
            {products.filter(p => p.category === category).map(p => (
              <article key={p.id} className="din-prod">
                <div className="din-prod-body">
                  <div className="din-prod-line">
                    <h3>{p.name}</h3>
                    <span className="din-prod-price">{euro(p.price)}</span>
                  </div>
                  {p.description && <p className="din-prod-desc">{p.description}</p>}
                </div>
                <button
                  className={`din-prod-add ${addedId === p.id ? 'ok' : ''}`}
                  onClick={() => handleAdd(p)}
                  aria-label={`Ajouter ${p.name}`}
                >
                  {addedId === p.id ? <IconCheck size={18} /> : <IconPlus size={18} />}
                </button>
              </article>
            ))}
          </div>
        </>
      )}

      {/* ---- NOTRE TABLE ---- */}
      {view === 'table' && (
        <div className="din-tab">
          {(!tab || tabOrders.length === 0) && (
            <div className="din-tab-vide">
              <span>🍽️</span>
              <p>Rien n'a encore été commandé sur cette table.</p>
              <button className="btn btn-or" onClick={() => setView('menu')}>Voir la carte</button>
            </div>
          )}

          {tab && tabOrders.map((o, idx) => {
            const st = STATUS_FR[o.status] || STATUS_FR.pending;
            return (
              <div key={o.id} className="din-envoi">
                <div className="din-envoi-head">
                  <strong>{o.guestLabel || `Envoi ${idx + 1}`}</strong>
                  <span className={`din-st ${st.class}`}>{st.label}</span>
                </div>
                {(o.items || []).map(it => (
                  <div key={it.id} className="din-envoi-item">
                    <span className="din-envoi-qty">{it.quantity}×</span>
                    <span className="din-envoi-name">
                      {it.name}
                      {it.notes && <em> — {it.notes}</em>}
                    </span>
                    <span className="din-envoi-price">
                      {euro(parseFloat(it.price) * it.quantity)}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}

          {tab && tabOrders.length > 0 && (
            <div className="din-total">
              <div className="din-total-ligne">
                <span>Total de la table</span>
                <strong>{euro(tab.total)}</strong>
              </div>
              <p className="din-total-info">
                Le règlement se fait auprès du serveur ou au comptoir, quand vous voulez.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ---- Panier du prochain envoi ---- */}
      {cartCount > 0 && !cartOpen && (
        <button className="din-cartbar" onClick={() => setCartOpen(true)}>
          <span className="din-cartbar-count">{cartCount}</span>
          Voir ma commande
          <strong>{euro(cartTotal)}</strong>
        </button>
      )}

      {cartOpen && (
        <div className="din-sheet-overlay" onClick={() => setCartOpen(false)}>
          <div className="din-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="din-sheet-grip" />
            <h2>Ma commande</h2>

            {cart.map(l => (
              <div key={l.uid} className="din-sheet-line">
                <div className="din-sheet-line-top">
                  <span className="din-sheet-name">{l.name}</span>
                  <span className="din-sheet-price">{euro(parseFloat(l.price) * l.quantity)}</span>
                </div>
                <div className="din-sheet-line-bottom">
                  <div className="din-qty">
                    <button onClick={() => setQty(l.uid, l.quantity - 1)}><IconMinus size={14} /></button>
                    <span>{l.quantity}</span>
                    <button onClick={() => setQty(l.uid, l.quantity + 1)}><IconPlus size={14} /></button>
                  </div>
                  <input
                    type="text"
                    placeholder="Une précision pour la cuisine ?"
                    value={l.notes}
                    onChange={(e) => setCart(prev => prev.map(x => x.uid === l.uid ? { ...x, notes: e.target.value } : x))}
                  />
                </div>
              </div>
            ))}

            <input
              type="text"
              className="din-guest"
              placeholder="Votre prénom (pour le service)"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              maxLength={50}
            />

            {error && <p className="din-error">{error}</p>}

            <button className="btn btn-or btn-block btn-lg" disabled={sending} onClick={send}>
              {sending ? 'Envoi…' : `Envoyer en cuisine — ${euro(cartTotal)}`}
            </button>
            <button className="din-sheet-close" onClick={() => setCartOpen(false)}>Continuer à choisir</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DineInPage;
